import { assignInlineVars } from '@vanilla-extract/dynamic';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import * as styles from './calendar-scheduler.css';
import { EventLayer } from './event-layer';
import { TimeGrid, WeekTimeGrid } from './time-grid';
import { Toolbar } from './toolbar';
import {
  CALENDAR_VIEW_MODE,
  type CalendarEvent,
  type CalendarSchedulerProps,
  type CalendarViewMode,
  type TimeSlotClickHandler,
} from './types';
import { formatDateKey, getWeekDates } from './utils';
import { WeekEventLayer } from './week-event-layer';

export const CalendarScheduler: React.FC<CalendarSchedulerProps> = ({
  events = [],
  onEventCreate,
  onEventUpdate,
  onEventDelete,
  initialDate = new Date(),
  className,
  onDateChange,
  onToday,
  compact = false,
  renderEvent,
  scrollToTime,
  dragSnapMinutes,
  alwaysShowCurrentTime = false,
  externalDropIdPrefix,
  suppressSlotHover = false,
  viewMode: controlledViewMode,
  onViewModeChange,
  enableWeekView,
  hideToolbar = false,
}) => {
  // Use initialDate directly instead of maintaining separate state
  const currentDate = initialDate;
  const defaultEventDurationMinutes = 60;
  const isWeekToggleEnabled = enableWeekView ?? true;
  const [internalViewMode, setInternalViewMode] =
    useState<CalendarViewMode>(CALENDAR_VIEW_MODE.DAY);
  const viewMode = isWeekToggleEnabled
    ? (controlledViewMode ?? internalViewMode)
    : CALENDAR_VIEW_MODE.DAY;
  const setViewMode = onViewModeChange ?? setInternalViewMode;

  const [scrollToTimeOverride, setScrollToTimeOverride] = useState<
    Date | undefined
  >(undefined);

  const effectiveScrollToTime = scrollToTime ?? scrollToTimeOverride;

  useEffect(() => {
    if (!scrollToTimeOverride) return;
    const rafId = requestAnimationFrame(() => {
      setScrollToTimeOverride(undefined);
    });
    return () => cancelAnimationFrame(rafId);
  }, [scrollToTimeOverride]);

  const jumpToToday = useCallback(() => {
    if (onToday) {
      onToday();
      return;
    }
    const today = new Date();
    onDateChange?.(today);
    setScrollToTimeOverride(today);
  }, [onDateChange, onToday]);

  const handleDateChange = useCallback(
    (days: number) => {
      const newDate = new Date(currentDate);
      const step = viewMode === CALENDAR_VIEW_MODE.WEEK ? 7 : 1;
      newDate.setDate(newDate.getDate() + days * step);
      onDateChange?.(newDate);
    },
    [currentDate, onDateChange, viewMode]
  );

  const handleTimeSlotClick: TimeSlotClickHandler = useCallback(
    (hour: number, minute: number) => {
      if (!onEventCreate) return;
      const startMinutes = hour * 60 + minute;
      const endMinutes = Math.min(
        startMinutes + defaultEventDurationMinutes,
        24 * 60
      );
      const startHour = Math.floor(startMinutes / 60);
      const startMin = startMinutes % 60;
      const endHour = Math.floor(endMinutes / 60);
      const endMin = endMinutes % 60;
      const date = currentDate;
      const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

      onEventCreate({
        title: 'Focus block',
        date: dateStr,
        startTime: `${startHour.toString().padStart(2, '0')}:${startMin.toString().padStart(2, '0')}`,
        endTime: `${endHour.toString().padStart(2, '0')}:${endMin.toString().padStart(2, '0')}`,
        category: 'work',
      });
    },
    [currentDate, onEventCreate, defaultEventDurationMinutes]
  );

  const handleWeekSlotClick = useCallback(
    (date: Date, hour: number, minute: number) => {
      if (!onEventCreate) return;
      const startMinutes = hour * 60 + minute;
      const endMinutes = Math.min(
        startMinutes + defaultEventDurationMinutes,
        24 * 60
      );
      const startHour = Math.floor(startMinutes / 60);
      const startMin = startMinutes % 60;
      const endHour = Math.floor(endMinutes / 60);
      const endMin = endMinutes % 60;
      const dateStr = formatDateKey(date);

      onEventCreate({
        title: 'Focus block',
        date: dateStr,
        startTime: `${startHour.toString().padStart(2, '0')}:${startMin.toString().padStart(2, '0')}`,
        endTime: `${endHour.toString().padStart(2, '0')}:${endMin.toString().padStart(2, '0')}`,
        category: 'work',
      });
    },
    [onEventCreate, defaultEventDurationMinutes]
  );

  const handleUpdateEvent = useCallback(
    (event: CalendarEvent) => {
      onEventUpdate?.(event);
    },
    [onEventUpdate]
  );

  const handleDeleteEvent = useCallback(
    (id: string) => {
      onEventDelete?.(id);
    },
    [onEventDelete]
  );

  const handleEditEvent = useCallback((_event: CalendarEvent) => {}, []);

  const dayKey = formatDateKey(currentDate);
  const weekDates = useMemo(() => getWeekDates(currentDate), [currentDate]);
  const weekDateKeys = useMemo(
    () => new Set(weekDates.map(date => formatDateKey(date))),
    [weekDates]
  );
  const currentDateEvents = useMemo(() => {
    if (viewMode === CALENDAR_VIEW_MODE.WEEK) {
      return events.filter(event => weekDateKeys.has(event.date));
    }
    return events.filter(event => event.date === dayKey);
  }, [dayKey, events, viewMode, weekDateKeys]);

  const slotHeight = compact ? 32 : 40;
  const variables = assignInlineVars({
    [styles.vars.timeSlotHeight]: `${slotHeight}px`,
    [styles.vars.sidebarWidth]: compact ? '42px' : '52px',
  });

  return (
    <div
      className={`${styles.calendarSchedulerRoot} ${className || ''}`}
      style={variables}
      data-view-mode={viewMode}
    >
      <div className={styles.calendarContent}>
        <div className={styles.calendarGrid} data-calendar-scroll="true">
          {viewMode === CALENDAR_VIEW_MODE.WEEK ? (
            <>
              <WeekTimeGrid
                onTimeSlotClick={handleWeekSlotClick}
                weekDates={weekDates}
                currentDate={currentDate}
                scrollToTime={effectiveScrollToTime}
                alwaysShowCurrentTime={alwaysShowCurrentTime}
                externalDropIdPrefix={externalDropIdPrefix}
                suppressSlotHover={suppressSlotHover}
                slotHeight={slotHeight}
              />
              <WeekEventLayer
                events={currentDateEvents}
                weekDates={weekDates}
                onEventEdit={handleEditEvent}
                onEventUpdate={handleUpdateEvent}
                onEventDelete={handleDeleteEvent}
                renderEvent={renderEvent}
                dragSnapMinutes={dragSnapMinutes}
              />
            </>
          ) : (
            <>
              <TimeGrid
                onTimeSlotClick={handleTimeSlotClick}
                currentDate={currentDate}
                scrollToTime={effectiveScrollToTime}
                alwaysShowCurrentTime={alwaysShowCurrentTime}
                externalDropIdPrefix={externalDropIdPrefix}
                suppressSlotHover={suppressSlotHover}
                slotHeight={slotHeight}
              />
              <EventLayer
                events={currentDateEvents}
                onEventEdit={handleEditEvent}
                onEventUpdate={handleUpdateEvent}
                onEventDelete={handleDeleteEvent}
                renderEvent={renderEvent}
                dragSnapMinutes={dragSnapMinutes}
              />
            </>
          )}
        </div>
      </div>
      {!hideToolbar && (
        <Toolbar
          currentDate={currentDate}
          onPrevDay={() => handleDateChange(-1)}
          onNextDay={() => handleDateChange(1)}
          onToday={jumpToToday}
          onViewModeChange={
            isWeekToggleEnabled
              ? mode => {
                  setViewMode(mode);
                  jumpToToday();
                }
              : undefined
          }
          viewMode={viewMode}
          enableWeekView={isWeekToggleEnabled}
        />
      )}
    </div>
  );
};
